import React from 'react';
import { PhoneOutgoing, Clock, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import { useCallbacks, fmtSyd } from '../../hooks/useCallbacks';
import { displaySummary } from '../../lib/summary';
import { ownerLabel } from '../../lib/callbackOwner';   // Option B (2026-09-23)

// Queue-wide scheduled-callbacks backlog (Task tab). Due tasks normally ring
// the queue as scheduled tasks by themselves — "Call now" is the recovery
// path for missed/overdue callbacks, and dials via Streams.
export default function CallbacksPanel() {
  // Incident 2026-09-14 fix 1: only a live VOICE leg blocks dialling; an
  // accepted callback task (also a connected contact) must not.
  const { agentConnected, voiceActive, handleDialNumber, agentUsername } = useContact();
  const cb = useCallbacks(agentConnected);

  if (!agentConnected) return null;
  const { err, loaded, due, upcoming, done, load } = cb;

  // 2026-09-18: the sweeper's terminal state for an exhausted callback is
  // "missed-needs-manual-followup" (knktr-callback-sweeper/index.mjs) — the
  // one status literally named for needing a human to act, but the DONE row
  // below gave no way to. Reuses the exact same dial button/onClick shape
  // Row already uses for "due" — no new wiring, no backend change.
  const NEEDS_FOLLOWUP = 'missed-needs-manual-followup';

  return (
    <div className="cb-panel">
      <div className="cb-head">
        <span className="cb-title"><Clock size={13} /> Scheduled callbacks</span>
        <button className="cb-refresh" onClick={load} title="Refresh"><RefreshCw size={12} /></button>
      </div>
      {err && <div className="cb-err">{err}</div>}
      {!loaded && !err && <div className="cb-empty">Loading…</div>}
      {loaded && due.length + upcoming.length === 0 && !err && (
        <div className="cb-empty">No callbacks in the queue.</div>
      )}
      {due.length > 0 && <div className="cb-group-label">Due now</div>}
      {due.map(r => (
        <Row key={r.callbackId} r={r} due canDial={!voiceActive} onDial={handleDialNumber} agentUsername={agentUsername} />
      ))}
      {upcoming.length > 0 && <div className="cb-group-label">Upcoming</div>}
      {upcoming.map(r => (
        <Row key={r.callbackId} r={r} canDial={false} onDial={handleDialNumber} agentUsername={agentUsername} />
      ))}
      {done.length > 0 && <div className="cb-group-label">Done</div>}
      {done.map(r => {
        const needsFollowup = r.status === NEEDS_FOLLOWUP;
        return (
          <div key={r.callbackId} className="cb-row cb-done">
            <div className="cb-row-main">
              <span className="cb-num">
                {r.customerNumber}
                <Owner r={r} agentUsername={agentUsername} />
              </span>
              <span className="cb-when">
                {needsFollowup ? <AlertTriangle size={11} /> : <Check size={11} />} {r.status}
              </span>
            </div>
            {needsFollowup && (
              <div className="cb-row-foot">
                <button
                  className="cb-dial"
                  disabled={voiceActive}
                  title={voiceActive ? 'Finish the current call first' : 'Call this customer now'}
                  onClick={() =>
                    handleDialNumber(r.customerNumber, {
                      tenantId: r.tenantId ?? null,
                      brandName: r.brandName ?? null,
                      callbackId: r.callbackId ?? null,
                      originalContactId: r.originalContactId ?? null,
                      handover: { aiSummary: r.aiSummary ?? null, reason: r.reason ?? null, window: r.dispatchAt ? fmtSyd(r.dispatchAt) : null, callerNumber: r.customerNumber ?? null },   // #20
                    })
                  }
                >
                  <PhoneOutgoing size={12} /> Call now
                </button>
              </div>
            )}
            {/* 2026-09-17: connect-gap flag — NOT a "customer didn't answer"
                claim, just "this connected slowly, might be worth a look."
                Never changes r.status; see docs/ops/callback-ownership-
                tasklist-investigation-2026-09-17.md. */}
            {Number(r.connectSeconds) >= 20 && (
              <div className="cb-slow-connect">
                <AlertTriangle size={11} />
                Connected in {r.connectSeconds}s — may not have reached a person
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Option B (2026-09-23, vault #233): whose callback this is. "Assigned to you"
// / "Assigned to <Name>" / "Anyone available" — see lib/callbackOwner.js.
function Owner({ r, agentUsername }) {
  const o = ownerLabel(r, agentUsername);
  return <span className={`cb-owner cb-owner-${o.kind}`}>{o.text}</span>;
}

function Row({ r, due, canDial, onDial, agentUsername }) {
  return (
    <div className={`cb-row${due ? ' cb-due' : ''}`}>
      <div className="cb-row-main">
        <span className="cb-num">
          {r.customerNumber}
          <Owner r={r} agentUsername={agentUsername} />
          {/* 2026-09-17: rows can now come from more than one entitled
              tenant (see docs/ops/callback-ownership-tasklist-investigation-
              2026-09-17.md) — a visible label so a merged list isn't
              ambiguous about whose callback this is. */}
          {r.tenantId && <span className="cb-tenant">{r.tenantId}</span>}
        </span>
        <span className="cb-when">{fmtSyd(r.dispatchAt)}</span>
      </div>
      {displaySummary(r.aiSummary) && <div className="cb-sum">{displaySummary(r.aiSummary)}</div>}
      <div className="cb-row-foot">
        <span className="cb-reason">{r.reason || ''}</span>
        {due && (
          <button
            className="cb-dial"
            disabled={!canDial}
            title={canDial ? 'Dial this customer now' : 'Finish the current call first'}
            onClick={() =>
              // D8 root cause: Streams dialling can't set contact attributes,
              // so hand the row's identity through for the ACW wrap-up.
              onDial(r.customerNumber, {
                tenantId: r.tenantId ?? null,
                brandName: r.brandName ?? null,
                callbackId: r.callbackId ?? null,
                originalContactId: r.originalContactId ?? null,
                handover: { aiSummary: r.aiSummary ?? null, reason: r.reason ?? null, window: r.dispatchAt ? fmtSyd(r.dispatchAt) : null, callerNumber: r.customerNumber ?? null },   // #20
              })
            }
          >
            <PhoneOutgoing size={12} /> Call now
          </button>
        )}
      </div>
    </div>
  );
}
